/**
 * Clip File Selector Component
 * Lists available clip data files and allows selection
 */

import React, { useEffect } from 'react';
import { useClipDataStore } from '../../stores/clipDataStore';
import { formatDistanceToNow } from 'date-fns';
import { CLIP_MONITOR_IS_REMOTE } from '../../lib/api-clip-monitor';

const CLIP_SERVER_OFFLINE_RETRY_MS = 30000;

export interface ClipFileSelectorProps {
  hideClipSelect?: boolean;
}

export const ClipFileSelector: React.FC<ClipFileSelectorProps> = ({ hideClipSelect = false }) => {
  const {
    availableClips,
    loadingClips,
    selectedClipAddress,
    serverOnline,
    checkingServer,
    checkServer,
    availableSources,
    activeSourceId,
    loadingSources,
    loadSources,
    switchSource,
    availableBuckets,
    activeBucket,
    loadingBuckets,
    loadBuckets,
    switchBucket,
    loadClipFiles,
    selectClip,
    deleteClip,
  } = useClipDataStore();

  useEffect(() => {
    if (serverOnline) {
      loadSources().catch(() => {});
      loadBuckets().catch(() => {});
      loadClipFiles().catch(() => {});
    }
  }, [serverOnline, loadSources, loadBuckets, loadClipFiles]);

  // When clip server is offline (e.g. ngrok down), retry periodically so we recover when it comes back
  useEffect(() => {
    if (serverOnline) return;
    const t = setInterval(() => checkServer(), CLIP_SERVER_OFFLINE_RETRY_MS);
    return () => clearInterval(t);
  }, [serverOnline, checkServer]);

  const handleSelect = (clipAddress: string | null) => {
    selectClip(clipAddress);
  };

  const handleDelete = async (clipAddress: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm(`Delete all data for clip ${clipAddress}?`)) {
      return;
    }

    try {
      await deleteClip(clipAddress);
    } catch (error) {
      alert(`Failed to delete clip data: ${error}`);
    }
  };

  const handleRefresh = () => {
    loadClipFiles().catch(() => {});
  };

  if (!serverOnline) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
        <p className="text-amber-800 font-semibold">Clip server unavailable</p>
        {CLIP_MONITOR_IS_REMOTE ? (
          <>
            <p className="text-amber-700 text-sm mt-2">
              Protected clip data is served from an on-demand server. The server may be offline.
            </p>
            <p className="text-amber-700 text-sm mt-1">
              Contact <strong>Sune</strong> to request the clip server to be started so you can access clip data.
            </p>
          </>
        ) : (
          <p className="text-amber-700 text-sm mt-2">
            Make sure the clip listener server is running on port 20042.
          </p>
        )}
        <div className="mt-3 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => checkServer()}
            disabled={checkingServer}
            className="px-3 py-1.5 bg-amber-200 text-amber-900 rounded hover:bg-amber-300 text-sm font-medium disabled:opacity-50"
          >
            {checkingServer ? 'Checking…' : 'Retry'}
          </button>
          {CLIP_MONITOR_IS_REMOTE && (
            <span className="text-amber-600 text-xs">Auto-retrying every 30s</span>
          )}
        </div>
      </div>
    );
  }

  const selectedClip = availableClips.find((c) => c.clipAddress === selectedClipAddress);
  const selectedAnnotationCount = selectedClip?.annotationCount ?? 0;

  const isLoading = loadingClips || loadingBuckets || loadingSources;

  const hasMultipleSources = availableSources.length > 1;

  return (
    <div className={`bg-white rounded-lg shadow-md px-3 py-2 transition-opacity ${isLoading ? 'opacity-80' : ''}`}>
      <div className={`flex items-center gap-2 ${hideClipSelect ? 'justify-start' : ''}`}>
        {hasMultipleSources && (
          <>
            <label className="text-xs font-semibold text-gray-600 shrink-0">Source</label>
            <select
              value={activeSourceId || ''}
              onChange={(e) => switchSource(e.target.value)}
              disabled={isLoading}
              className="text-xs py-1 px-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary bg-green-50 font-medium"
            >
              {availableSources.map((s) => (
                <option key={s.id} value={s.id} disabled={!s.available}>
                  {s.label}{!s.available ? ' (unavailable)' : ''}
                </option>
              ))}
            </select>
            <span className="text-gray-300">|</span>
          </>
        )}
        {availableBuckets.length > 0 && (
          <>
            <label className="text-xs font-semibold text-gray-600 shrink-0">Dataset</label>
            <select
              value={activeBucket || ''}
              onChange={(e) => switchBucket(e.target.value || null)}
              disabled={isLoading}
              className="text-xs py-1 px-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary bg-blue-50 font-medium"
            >
              <option value="">(root)</option>
              {availableBuckets.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            {!hideClipSelect && <span className="text-gray-300">|</span>}
          </>
        )}
        
        {!hideClipSelect && (
          <>
            <label className="text-xs font-semibold text-gray-600 shrink-0">Clips</label>
            <div className="relative flex-1 min-w-0">
              <select
                value={selectedClipAddress || ''}
                onChange={(e) => handleSelect(e.target.value || null)}
                disabled={isLoading}
                className={`w-full text-xs font-mono py-1 px-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary truncate ${isLoading ? 'text-gray-400' : ''}`}
              >
                <option value="">{isLoading ? 'Loading clips...' : 'Select clip...'}</option>
                {[...availableClips]
                  .sort((a, b) => {
                    const annDiff = (b.annotationCount ?? 0) - (a.annotationCount ?? 0);
                    if (annDiff !== 0) return annDiff;
                    const labelA = a.displayLabel || a.clipAddress;
                    const labelB = b.displayLabel || b.clipAddress;
                    return labelA.localeCompare(labelB);
                  })
                  .map((clip) => (
                  <option key={clip.clipAddress} value={clip.clipAddress}>
                    {clip.displayLabel ? `${clip.displayLabel} [${clip.clipAddress}]` : clip.clipAddress}
                    {(clip.annotationCount ?? 0) > 0 && ` 📌 ${clip.annotationCount}`}
                    {` • ${clip.recordCount.toLocaleString()} rec • ${formatDistanceToNow(new Date(clip.lastUpdate), { addSuffix: true })}`}
                  </option>
                ))}
              </select>
              {isLoading && (
                <div className="absolute right-6 top-1/2 -translate-y-1/2">
                  <svg className="animate-spin h-3.5 w-3.5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
            </div>
            {selectedAnnotationCount > 0 && (
              <span
                className="shrink-0 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded font-medium"
                title={`${selectedAnnotationCount} annotation(s)`}
              >
                📌 {selectedAnnotationCount}
              </span>
            )}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className={`shrink-0 px-2 py-1 text-xs rounded disabled:opacity-50 ${isLoading ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 hover:bg-gray-200'}`}
              title="Refresh list"
            >
              {isLoading ? (
                <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : '↻'}
            </button>
            {selectedClipAddress && (
              <button
                onClick={(e) => handleDelete(selectedClipAddress, e)}
                className="shrink-0 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                title="Delete clip data"
              >
                🗑
              </button>
            )}
          </>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 mt-1.5 py-1 px-1 bg-blue-50 rounded text-xs text-blue-700">
          <svg className="animate-spin h-3 w-3 text-blue-500 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>{loadingBuckets ? 'Loading datasets...' : `Loading clips from ${activeBucket || 'root'}...`}</span>
        </div>
      )}

      {availableClips.length === 0 && !isLoading && !hideClipSelect && (
        <p className="text-xs text-gray-500 mt-1">No clip data yet</p>
      )}
    </div>
  );
};

